/**
 * SUB-5.15 · FR44, FR48. The two things this portfolio's data can honestly say.
 *
 * ⚠️ **THIS DECIDES WHAT IS SHOWN. IT BOUNDS NOTHING.** Same standing rule as
 * `mergedMetrics.ts`, `portfolioRollup.ts`, `portfolioFigureProjects.ts` and
 * `accountAttribution.ts`: the accounts a surface may span is
 * `merged_scope_tenant_ids()` evaluated inside the policy, and nothing here may
 * ever be the reason a row is absent.
 *
 * ---------------------------------------------------------------------------
 * §1 · WHY THIS MODULE EXISTS: EVERY COUNT ON THIS DATA IS SATURATED
 *
 * Measured live 2026-08-18 across the four accounts with real volume:
 *
 * | | |
 * |---|---|
 * | past SLA, as a share of what is measurable | **95%** (three accounts at 100%) |
 * | no stage movement in 30 days | **96%** |
 * | not measurable at all | **68%** |
 *
 * A tile reading "1,455 past SLA" is true, useless, and hides the finding.
 * `portfolioRollup.ts` §1 reached the same conclusion for the ranking and chose
 * a relative ordering over absolute counts. This module is the other half: the
 * two quantities on this data that are NOT saturated.
 *
 * ---------------------------------------------------------------------------
 * §2 · ⚠️ COVERAGE IS OVER ACTIVE WORK, AND MEASURING IT OVER EVERYTHING LIES
 *
 * The first version of this counted every project. Measured against the live
 * catalog, the largest "blind spots" it produced were:
 *
 *     Solerro · New Leads            1,158
 *     Quality Solar · Project Complete 347
 *     Testing · New Leads              311
 *     Southeast Energy · Cancelled     295
 *
 * ⚠️ **EVERY ONE OF THOSE IS A STAGE WHERE AN SLA IS CORRECTLY ABSENT.** Nobody
 * puts a clock on a cancelled project or an untouched lead. Reporting them as
 * gaps would nag the user daily about a non-problem, which is exactly how a
 * dashboard teaches people to ignore it.
 *
 * Restricted to ACTIVE projects the picture changes materially, and one account
 * changes verdict:
 *
 *     Solerro            338 of 2,231   15%
 *     Quality Solar      448 of 1,006   45%
 *     Southeast Energy   363 of   413   88%   <- was 52% over all projects
 *     Testing            246 of   557   44%
 *
 * ⚠️ **"Active" IS `projectStatusVocabulary`'s CANONICAL BUCKET, NEVER A RAW
 * SPELLING.** `projects.project_status` is free text carrying nine spellings
 * over nine accounts, which is F-45. An equality test on one spelling would drop
 * every account that spells it differently.
 *
 * ---------------------------------------------------------------------------
 * §3 · ⚠️ MOVEMENT NEEDS NO MIGRATION, AND I SAID IT DID
 *
 * The design review recorded that throughput required `project_stage_history`,
 * whose SELECT policy is `tenant_id = get_current_user_tenant_id()` and would
 * therefore need widening. **That is true only for a trend over time.** The
 * question "did this project move in the last 30 days" is answered by
 * `projects.stage_changed_at`, a platform column the portfolio already reads.
 *
 * It is the one quantity here that discriminates sharply, measured the same day:
 *
 *     Quality Solar   191 moved
 *     Thryve Demo       2
 *     Solerro           1
 *     Southeast Energy  0
 *     Testing           0
 *
 * A history table is still what a WEEKLY trend would need. Nothing on this
 * screen needs one.
 */

import {
  isCounted,
  type PortfolioProjectRow,
  type PortfolioTaskRow,
} from './portfolioMetrics';
import { overrunDays, type ProjectSlaRow } from './portfolioRollup';
import { canonicalProjectStatus } from './projectStatusVocabulary';

/** How much of an account's live work the SLA metric can actually see. */
export interface AccountCoverage {
  tenantId: string;
  /** Active projects, by the canonical bucket. The denominator. */
  active: number;
  /** Of those, the ones sitting in a stage with an SLA configured. */
  measured: number;

  /**
   * ⚠️ **THERE IS DELIBERATELY NO `percent` FIELD. D93.** This carried one, the
   * page rendered it as a ring gauge reading "15%", and Allan's first reaction
   * was that it was not clear what the number meant. He was right twice over: a
   * bare percentage does not name its denominator, and a gauge implies a target
   * that does not exist here.
   *
   * The surface states the fraction with its noun instead — "338 of 2,231 live
   * projects have an SLA" — which cannot be misread. The field is gone rather
   * than left unread, because a computed value nothing consumes is an invitation
   * to render it again, and that is the shape SUB-5.90 found when SUB-5.4's
   * registry turned out to be called by nothing.
   */
}

/**
 * ⚠️ AN ACCOUNT WITH NO ACTIVE WORK IS ABSENT FROM THE RESULT, not present with
 * a zero. "None of your live work is measurable" is a finding; "you have no live
 * work" is not, and rendering the second as the first puts a red figure on an
 * account that has done nothing wrong.
 */
export function coverageByAccount(
  slaRows: readonly ProjectSlaRow[],
  projectRows: readonly PortfolioProjectRow[],
): AccountCoverage[] {
  const measurable = new Set(
    slaRows.filter((row) => (row.breach_days ?? 0) > 0).map((row) => row.project_id),
  );

  const byAccount = new Map<string, { active: number; measured: number }>();

  for (const row of projectRows) {
    if (!isCounted(row)) continue;
    if (canonicalProjectStatus(row.project_status) !== 'active') continue;

    let acc = byAccount.get(row.tenant_id);
    if (!acc) {
      acc = { active: 0, measured: 0 };
      byAccount.set(row.tenant_id, acc);
    }
    acc.active += 1;
    if (measurable.has(row.id)) acc.measured += 1;
  }

  return [...byAccount.entries()].map(([tenantId, acc]) => ({
    tenantId,
    active: acc.active,
    measured: acc.measured,
  }));
}

/** A stage holding live work nobody can measure. */
export interface BlindSpot {
  tenantId: string;
  stageId: number;
  /** Active projects in this stage with no SLA configured on it. */
  projects: number;
}

/**
 * The stages where live work is invisible to the SLA metric, largest first.
 *
 * ⚠️ **ATTRIBUTED TO A STAGE, NOT TO AN ACCOUNT, BECAUSE THAT IS THE FIXABLE
 * UNIT.** "Solerro is 15% covered" is a complaint nobody can act on. "Solerro ·
 * Permitting holds 1,204 live projects and has no SLA" names the row someone
 * opens and edits.
 *
 * ⚠️ **THE STAGE NAME IS NOT RESOLVED HERE.** `stages_select` is single-tenant,
 * so a sub-account's stage cannot be named through a plain read at all; the
 * caller resolves ids through `merged_scope_stage_names`, which is SUB-4.15's
 * function and carries its own withheld state. Returning ids keeps this module
 * pure and keeps the one permission rule in one place.
 */
export function blindSpotsByStage(
  slaRows: readonly ProjectSlaRow[],
  projectRows: readonly PortfolioProjectRow[],
  limit = 5,
): BlindSpot[] {
  const measurable = new Set(
    slaRows.filter((row) => (row.breach_days ?? 0) > 0).map((row) => row.project_id),
  );

  const byStage = new Map<string, BlindSpot>();

  for (const row of projectRows) {
    if (!isCounted(row)) continue;
    if (canonicalProjectStatus(row.project_status) !== 'active') continue;
    if (measurable.has(row.id)) continue;
    // A project with no stage at all is a different problem and has no stage row
    // to go and fix, so it is not a blind SPOT.
    if (row.current_stage_id == null) continue;

    const key = `${row.tenant_id}:${row.current_stage_id}`;
    const existing = byStage.get(key);
    if (existing) {
      existing.projects += 1;
    } else {
      byStage.set(key, {
        tenantId: row.tenant_id,
        stageId: row.current_stage_id,
        projects: 1,
      });
    }
  }

  return [...byStage.values()]
    .sort((a, b) => b.projects - a.projects)
    .slice(0, limit);
}

/** How much an account actually moved, which is the one figure that varies. */
export interface AccountMovement {
  tenantId: string;
  /** Projects whose stage changed inside the window. */
  moved: number;
  /** Live projects in the account, as the denominator. */
  active: number;
}

export function movementByAccount(
  projectRows: readonly PortfolioProjectRow[],
  windowDays: number,
  now: Date = new Date(),
): AccountMovement[] {
  const cutoff = now.getTime() - windowDays * 86_400_000;
  const byAccount = new Map<string, AccountMovement>();

  for (const row of projectRows) {
    if (!isCounted(row)) continue;
    if (canonicalProjectStatus(row.project_status) !== 'active') continue;

    let acc = byAccount.get(row.tenant_id);
    if (!acc) {
      acc = { tenantId: row.tenant_id, moved: 0, active: 0 };
      byAccount.set(row.tenant_id, acc);
    }
    acc.active += 1;

    const changed = row.stage_changed_at ? Date.parse(row.stage_changed_at) : NaN;
    // ⚠️ A NULL `stage_changed_at` IS NOT "DID NOT MOVE". The clock is
    // unreadable, which portfolioRollup.ts §2 keeps as a third state everywhere
    // else. Counting it as stationary would overstate how stuck an account is.
    if (Number.isFinite(changed) && changed >= cutoff) acc.moved += 1;
  }

  return [...byAccount.values()];
}

/** One row of the triage queue: a project worth touching today. */
export interface TriageRow {
  projectId: string;
  tenantId: string;
  projectName: string | null;
  stageId: number | null;
  /** Days past this account's own threshold. Always positive. */
  overrun: number;
}

/**
 * The worst work in the portfolio, worst first, named.
 *
 * ⚠️ **ACROSS ACCOUNTS RATHER THAN GROUPED BY THEM, ON PURPOSE.** The whole
 * point of a portfolio queue is that the worst project surfaces wherever it
 * lives; grouping by account would bury a 451-day overrun under a healthier
 * client's rows.
 *
 * ⚠️ **THE THRESHOLD IS EACH ACCOUNT'S OWN.** `overrunDays` measures against
 * `breach_days` resolved per account by `merged_scope_project_sla()` (D45 takes
 * that account's maximum), so ranking these together compares "days late", not
 * "days in stage". That is exactly what makes `past_sla` portable in
 * `mergedMetrics.ts` and it is the reason this list may be ranked at all.
 */
export function triageQueue(
  slaRows: readonly ProjectSlaRow[],
  projectRows: readonly PortfolioProjectRow[],
  limit = 25,
): TriageRow[] {
  const byId = new Map(projectRows.map((row) => [row.id, row]));

  return slaRows
    .filter((row) => row.is_past_sla === true)
    .map((row) => {
      const project = byId.get(row.project_id);
      return {
        projectId: row.project_id,
        tenantId: row.tenant_id,
        projectName: project?.project_name ?? null,
        stageId: row.stage_id,
        overrun: overrunDays(row) ?? 0,
      };
    })
    .sort((a, b) => b.overrun - a.overrun)
    .slice(0, limit);
}

/** How many projects are past SLA in total, so the queue can say what it truncated. */
export function pastSlaTotal(slaRows: readonly ProjectSlaRow[]): number {
  return slaRows.filter((row) => row.is_past_sla === true).length;
}

/** SUB-5.19 · D99. Open tasks, per organisation. */
export interface AccountTaskLoad {
  tenantId: string;
  /** Tasks that are not completed. */
  openItems: number;
  /** Distinct people carrying them, plus one for unassigned if any exist. */
  people: number;
  /** Open tasks nobody is assigned to. */
  unassigned: number;
}

/**
 * SUB-5.19 · D99. M3, re-cut by ORGANISATION rather than by person.
 *
 * ⚠️ **THIS REPLACES D50'S GROUPING RULE RATHER THAN IMPLEMENTING IT.** D50 said:
 * name the caller's own team, and group each client's staff under their account,
 * because `users_select` is single-tenant and a sub-account's people could not be
 * named. `merged_scope_user_names` (SUB-4.16) made that false —
 * `u.tenant_id = ANY (merged_scope_tenant_ids())` — so the grouping is no longer
 * forced by a permission boundary.
 *
 * It is kept anyway, as a CHOICE rather than a constraint: an agency admin thinks
 * about a client first and a person second, and a flat list of every person
 * across every account does not scale. The difference is that the people are now
 * named one level down instead of being unnameable.
 *
 * ⚠️ **"OPEN" IS NOT-COMPLETED, WHICH IS WHAT `getPortfolioTasks` FILTERS ON.**
 * Counting it any other way here would make the row disagree with the panel it
 * opens, which is the whole class of defect this epic keeps finding.
 */
export function openTasksByAccount(
  tasks: readonly PortfolioTaskRow[],
): AccountTaskLoad[] {
  const byAccount = new Map<string, { open: number; people: Set<string>; unassigned: number }>();

  for (const task of tasks) {
    if (task.status === 'completed' || task.completed_at) continue;

    let acc = byAccount.get(task.tenant_id);
    if (!acc) {
      acc = { open: 0, people: new Set<string>(), unassigned: 0 };
      byAccount.set(task.tenant_id, acc);
    }
    acc.open += 1;
    if (task.assignee_id) acc.people.add(task.assignee_id);
    else acc.unassigned += 1;
  }

  return [...byAccount.entries()]
    .map(([tenantId, acc]) => ({
      tenantId,
      openItems: acc.open,
      people: acc.people.size,
      unassigned: acc.unassigned,
    }))
    .sort((a, b) => b.openItems - a.openItems);
}

/**
 * SUB-5.24 · D100's RULE, EXTRACTED SO IT CAN BE TESTED.
 *
 * ⚠️ **THIS IS THE MOST LOAD-BEARING THREE LINES IN THE TASK HIERARCHY AND IT
 * LIVED INLINE IN TWO COMPONENTS.** `PortfolioRollup` and `TaskListDrawer` each
 * spelled it as `server ?? client`, which is correct and says nothing about WHY,
 * so nothing stopped a later edit from writing `server?.length ? server : client`
 * — which looks equivalent and is not.
 *
 * The rule:
 *
 *   * `null` means **the server did not answer**. Fall back.
 *   * `[]` means **the server answered, and the answer is nothing**. Use it.
 *
 * ⚠️ Collapsing those two is the silent subset NFR8 forbids: an unanswered
 * aggregate rendered as "no open tasks" is a confident, wrong, empty screen. It
 * is the same distinction `resolved` draws in `MergedScopeStatus`, `readAllRows`
 * draws between an error and a short page, and `stageNameCell` draws between
 * "withheld" and "unavailable". Four places, one idea.
 */
export interface AggregateChoice<T> {
  rows: T[];
  /** Which side answered. Useful for a caller that wants to say so. */
  source: 'server' | 'client';
}

export function chooseAggregate<T>(
  server: readonly T[] | null | undefined,
  client: readonly T[],
): AggregateChoice<T> {
  // ⚠️ `== null` catches null AND undefined, and nothing else. An empty array is
  // NOT nullish, so it correctly takes the server branch.
  if (server == null) return { rows: [...client], source: 'client' };
  return { rows: [...server], source: 'server' };
}

/**
 * SUB-5.24 · F-103's OTHER HALF, EXTRACTED.
 *
 * ⚠️ **THE PAGER ORDERS BY `id` AND THE CALLER WANTS `created_at` DESC, SO THE
 * SORT HAPPENS HERE, AFTER THE COMPLETE SET IS IN HAND.** Offset paging needs a
 * UNIQUE stable order or rows are skipped and repeated between requests, and
 * `created_at` ties. Paging by `created_at` would have preserved the visible
 * order and silently broken the walk, which is the more dangerous mistake of the
 * two because nothing looks wrong.
 *
 * ⚠️ **AN UNPARSEABLE DATE SORTS LAST, NOT FIRST.** `Date.parse` returns NaN,
 * and NaN in a comparator makes the sort order undefined — rows would move
 * between renders for no reason. Treating it as the oldest possible instant is a
 * decision rather than an accident.
 */
export function sortByCreatedDesc<T extends { created_at?: string | null }>(
  rows: readonly T[],
): T[] {
  const at = (row: T): number => {
    const parsed = row.created_at ? Date.parse(row.created_at) : NaN;
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  };
  return [...rows].sort((a, b) => at(b) - at(a));
}
